import React, { useState, useEffect, useRef, memo } from 'react';
import { useUnistyles } from 'react-native-unistyles';
import { Text, View, TextInput, Pressable, Platform } from 'react-native';
import { shade } from '@/utils/shade';
import { PickerList } from '@/components/General/inputs/PickerList';
import usePlatform from '@/utils/hooks/usePlatform';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '../Unicon';
import InlineHelpIcon from './InlineHelpIcon';


const Input = ({ 
    tabIndex,
    placeholder,
    value,
    description,
    helpText,
    detection = [], // ["mention", "tag", "link"]
    onUpdate,
    multiline = false,
    title,
    textColor,
    maxLength = undefined,
    type = "default",
    format = null, //options: currency
    secureTextEntry = false,
    allowSecureToggle = true,
    autoCorrect = true,
    autoComplete = "off",
    autoCapitalize = 'sentences',
    disabled = false,
    onEnter = null,
    prefixIcon = null,
    required = false,
    onFocus = () => {},
    onBlur = () => {},
    isNotOnSurface = false,
    testID = null,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [formattedValue, setFormattedValue] = useState(value);
    const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });

    const [showPicker, setShowPicker] = useState(null);
    const [pickerPosition, setPickerPosition] = useState(0);
    const [pickerTerm, setPickerTerm] = useState("");

    const input_ref = useRef();
    const isFirstRender = useRef(true);

    const {theme: {colors, scheme}} = useUnistyles();
    const { isWeb, isNative } = usePlatform();

    const PICKER_HEIGHT = 150;

    useEffect(() => {
        if(!isFirstRender){
            input_ref.current.focus();
        }
        else{
            isFirstRender.current = false;
        }

        if(detection.length != 0){
            const words = value?.split(' ');
            const formattedText = [];
            words?.forEach((word, index) => {
                const isLastWord = index === words.length - 1;
                if (isLastWord && word.startsWith('@') && detection?.includes("mention")) {
                    setShowPicker("mention")
                    setPickerTerm(word.substring(1))
                    formattedText.push(<Text key={word + index} style={styles.mentionText}>
                        {word}
                    </Text>)
                }
                else if (isLastWord && word.startsWith('#') && detection?.includes("tag")) {
                    setShowPicker("tag")
                    setPickerTerm(word.substring(1))
                    formattedText.push(<Text key={word + index} style={styles.tagText}>
                        {word}
                    </Text>)
                }
                else if (isLastWord && word.startsWith('&') && detection?.includes("link")) {
                    setShowPicker("link")
                    setPickerTerm(word.substring(1))
                    formattedText.push(<Text key={word + index} style={styles.linkText}>
                        {word}
                    </Text>)
                }
                else {
                    setShowPicker(null)
                    setPickerTerm("")
                    formattedText.push(word + (isLastWord ? "" : " "));
                }
            });

            setFormattedValue(formattedText)
        }
        else{
            setFormattedValue(value)
        }
    }, [value])

    const updateValue = (new_text, dont_trim = false) => {
        if(type == "numeric"){
            new_text = new_text.replace(/[^0-9]/g, '')
        }

        onUpdate(dont_trim ? new_text : new_text.trim())
    }

    const formatCurrency = (text) => {
        if(!text) return "";

        // Remove non-numeric characters except decimal
        let cleaned = text.toString().replace(/[^0-9.]/g, "");
    
        // Parse as float
        let num = parseFloat(cleaned);
    
        if (isNaN(num)) {
          return "";
        }
    
        // Format as USD
        return new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(num);
      };

    return (
        <View style={styles.container}>
            {showPicker &&
                <PickerList type={showPicker} position={pickerPosition} term={pickerTerm} onConfirm={() => {}} onDismiss={() => setShowPicker(null)} />
            }
            <View style={styles.labelContainer}>
                <Text style={styles.labelText}>
                    {title}
                    {required && <Text style={styles.required}>*</Text>}
                    {helpText && <InlineHelpIcon text={helpText} />}
                </Text>
            </View>
            <View style={styles.inputWrapper}>
                {prefixIcon &&
                    <View style={styles.prefixIconContainer(disabled)}>
                        <Unicon name={prefixIcon} size={20} colorVariant="textSecondary" />
                    </View>
                }

                <TextInput
                    tabIndex={tabIndex}
                    testID={testID || `Input-${title}`}
                    placeholder={placeholder || title}
                    placeholderTextColor={colors.textSecondary}
                    ref={input_ref}
                    inputMode={type}
                    autoCorrect={autoCorrect}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    autoCapitalize={autoCapitalize}
                    enterKeyHint={multiline ? null : "done"}
                    secureTextEntry={secureTextEntry ? !showPassword : false}
                    style={styles.input_container(textColor, multiline, prefixIcon, disabled, isNotOnSurface)}
                    multiline={multiline}
                    readOnly={disabled}
                    // defaultValue={detection.length > 0 ? undefined : value}
                    value={detection.length > 0 ? undefined : (value?.toString() || "")}
                    keyboardAppearance={scheme}
                    onFocus={(e) => {
                        input_ref.current.measure( (fx, fy, w, h, px, py) => {
                            if(py - PICKER_HEIGHT < 0){
                                setPickerPosition(py + h)
                            }
                            else{
                                setPickerPosition(py-PICKER_HEIGHT-10)
                            }

                        })

                        onFocus(e);
                    }}
                    onBlur={(e) => {
                        // if(value == undefined || value == ""){
                        //     moveTitleDown();
                        // }
                        if(format == "currency"){
                            updateValue(formatCurrency(value))
                        }

                        setPickerPosition(0);
                        setShowPicker(null);

                        onBlur(e);
                    }}
                    onSelectionChange={({ nativeEvent: { selection } }) => {
                        setCursorPosition(selection);
                    }}
                    onSubmitEditing={() => {
                        if(isWeb && onEnter){
                            onEnter();
                        }
                    }}
                    onChangeText={new_text => {
                        if (new_text.length > value?.length) {
                            const position = cursorPosition.start;
                            const beforePosition = new_text.slice(0, position);

                            if(type == "numeric" || type == "decimal"){
                                new_text = new_text.replace(/[^0-9.]/g, '').replace(/(?<=\..*)\./g, '')
                            }
                            
                            //Replace double spaces with a period (like native iOS) - state updates on text change prevent the native functionality
                            if(isNative && new_text.slice(position - 2, position) === '  ' && beforePosition.trim().slice(-1) != "."){
                                new_text = new_text.slice(0, position - 2) + '. ' + new_text.slice(position);
                            }
                        }

                        updateValue(new_text, true)
                    }}
                    
                >{detection.length > 0 ? formattedValue : undefined}</TextInput>

                
                <View style={styles.options}>
                    {(secureTextEntry && allowSecureToggle) &&
                        <Pressable hitSlop={8} onPress={() => setShowPassword(!showPassword)}>
                            <Unicon name={!showPassword ? "eye-outline" : "eye-off-outline"} size={20} colorVariant="textSecondary" />
                        </Pressable>
                    }
                    {(!disabled && ((value != undefined && value != "") || (Array.isArray(formattedValue) && formattedValue.filter(Boolean).length > 0) || (formattedValue != undefined && formattedValue != "" && !Array.isArray(formattedValue)))) &&
                        <Pressable hitSlop={{ top: 8, bottom: 8, right: 12, left: secureTextEntry && allowSecureToggle ? 0 : 12 }} onPress={() => { onUpdate(""); input_ref.current?.focus(); }}>
                            <Unicon name="close" size={18} colorVariant="textSecondary"/>
                        </Pressable>
                    }
                </View>
            </View>
            {description && <Text style={styles.description}>{description}</Text>}
        </View>
    );
}


const styles = StyleSheet.create(({colors, fonts, shadows, spacing, roundness, typography}, rt) => {
    return {
    container: {
        marginVertical: spacing.sm,
    },
    input_container: (textColor, multiline, prefixIcon, disabled, isNotOnSurface) => ({
        opacity: disabled ? 0.5 : 1,
        borderRadius: roundness.sm,
        width: "100%",
        color: textColor || colors.text,
        paddingHorizontal: 14,
        paddingTop: multiline ? 12 : (Platform.OS === 'android' ? 10 : 0),
        paddingLeft: prefixIcon ? 44 : 14,
        height: multiline ? Math.max(110, Math.ceil(110 * rt.fontScale)) : Math.max(44, Math.ceil(44 * rt.fontScale)),
        outlineStyle: 'none',
        fontSize: typography.body,
        fontFamily: fonts.body,
        includeFontPadding: false,
        textAlignVertical: multiline ? 'top' : 'center',
        backgroundColor: isNotOnSurface ? colors.surface : colors.inputBackground,
        boxShadow: isNotOnSurface ? shadows.card : shadows.none,
        _web: {
            transition: "all 0.15s ease",
            _focus: {
                boxShadow: disabled ? "" : shadows.input,
            },
            _hover: {
                backgroundColor: disabled ? (isNotOnSurface ? colors.surface : colors.inputBackground) : colors.hover,
            }
        }
    }),
    labelContainer: {
        paddingBottom: 6,
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
    },
    labelText: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
        flexShrink: 1,
    },
    placeholder: {
       position: "absolute",
       pointerEvents: "none",
       transformOrigin: 'left',
       top: 21,
       left: 15,
       zIndex: 3,
    },
    inputWrapper: {
        position: "relative",
    },
    options: {
        position: "absolute",
        right: 12,
        top: 0,
        bottom: 0,
        zIndex: 5,
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    prefixIconContainer: (disabled) => ({
        position: "absolute",
        left: 14,
        top: 0,
        bottom: 0,
        zIndex: 5,
        opacity: disabled ? 0.5 : 1,
        justifyContent: "center",
    }),
    required: {
        color: colors.error,
        fontSize: 14,
        fontFamily: fonts.body,
        marginLeft: 2,
    },
    mentionText: {
        backgroundColor: colors.secondary, 
        fontWeight: "bold", 
        color: "#ffffff",
    },
    tagText: {
        backgroundColor: colors.tertiary, 
        fontWeight: "bold", 
        color: "#ffffff",
    },
    linkText: {
        backgroundColor: shade(-0.5, colors.warning), 
        fontWeight: "bold", 
        color: "#ffffff",
    },
    description: {
        fontSize: typography.caption, 
        color: colors.textSecondary, 
        fontStyle: "italic", 
        paddingLeft: 10,
        paddingTop: 4,
    }
}
});

export default memo(Input)